import React, { useState, useEffect, useContext } from 'react'
import Layout from 'components/layout'
import SEO from 'components/seo'
import AnimatedHeroText from 'components/animatedHeroText'
import { graphql } from 'gatsby'
import { injectIntl, Link } from 'gatsby-plugin-intl'
import styled from 'styled-components'
import { colors, breakpoints, richTextOptions as options, transitions } from 'utils/utils'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import 'animate.css/animate.min.css'
import ScrollAnimation from 'react-animate-on-scroll'
import HeroContainer from 'components/heroContainer'
import { IoIosArrowDown } from 'react-icons/io'
import { GlobalStateContext } from '../context/globalContext'
import { GlobalDispatchContext } from '../context/globalContext'

export default injectIntl(props => {

    const locale = props.intl.locale

    const state = useContext(GlobalStateContext)
    const dispatch = useContext(GlobalDispatchContext)  
     
    useEffect(() => {
        if(state.otherLanguageSlug !== props.pageContext.otherLanguageSlug) {
            dispatch(state.changeOtherLanguageSlug(props.pageContext.otherLanguageSlug))
        }
    }, [state, dispatch, props.pageContext.otherLanguageSlug])
    
    const { media, data, cta, seo, title, animatedHeroText, listBlockSection } = props.data.contentfulSection
    
    const blogEntries = props.data.blogEntries

    const [ contentShowed, setContentShowed ] = useState("none")

    const handleViewMore = (e) => {
        var dataName = e.target.getAttribute("data-name")
        
        if (e.target.nodeName === 'svg' || e.target.nodeName === 'H3') {
            dataName = e.target.parentElement.getAttribute("data-name")
        }
        if (e.target.nodeName === 'path') {
            dataName = e.target.parentElement.parentElement.getAttribute("data-name")
        }
        if (dataName === contentShowed) {
            dataName = "none"
        }
        setContentShowed(dataName)
    }

    return (
        < >
            <Layout>
                <SEO 
                    title={seo.title}
                    keywords={seo.attributes[0].keywords}
                    slug={seo.slug}
                    description={seo.attributes[0].description}
                    lang={props.pageContext.language}
                />               
                <Container>
                    <HeroContainer>
                        <AnimatedHeroText data={animatedHeroText} image={media[0].fluid} />
                        <Title>
                            <h1>
                                <ScrollAnimation animateOnce={true} animateIn="fadeInUp">
                                    {title}
                                </ScrollAnimation>
                            </h1>
                        </Title>
                    </HeroContainer>


                    <CenterBlock>
                        <CenterBlockTitle>
                            <h2>
                                <ScrollAnimation animateOnce={true} animateIn="fadeInRight">
                                    <span>{data.mainSectionTitle}</span>
                                    <a href={cta[0].href} target="_blank" rel="noreferrer">{cta[0].value}</a>
                                </ScrollAnimation>
                            </h2>
                        </CenterBlockTitle>
                        <CenterBlockText>
                            <ScrollAnimation animateOnce={true} animateIn="fadeInLeft">
                                <ListBlockSection>
                                    {
                                        listBlockSection.map((listBlock, index) => {
                                            return (
                                                <ListBlock key={index} className={contentShowed === "content" + (index + 1) ? "show" : ""}>
                                                    <button aria-label={listBlock.title} onClick={handleViewMore} data-name={"content" + (index + 1)}>
                                                        <IoIosArrowDown />
                                                        <h3>{listBlock.title}</h3>
                                                    </button>
                                                    <div>
                                                        { documentToReactComponents(listBlock.text && listBlock.text.json, options) }
                                                    </div>
                                                </ListBlock>
                                            )
                                        })
                                    }
                                </ListBlockSection>
                            </ScrollAnimation>
                        </CenterBlockText>
                    </CenterBlock>
                    <BlogBlock>
                        <BlogTitle>
                            <ScrollAnimation animateOnce={true} animateIn="fadeInUp">
                                {data.blogSectionTitle}
                            </ScrollAnimation>
                        </BlogTitle>
                        <BlogEntries>
                            {
                                blogEntries.edges.map(({node: entry},index) => {
                                    return(
                                        <BlogEntry key={index} to={(`/${locale}/${entry.slug}`).toLowerCase().trim()}>
                                            <ScrollAnimation animateOnce={true} animateIn="fadeInUp">
                                                <h2>{entry.title}</h2>
                                                {entry.summary ? <p>{entry.summary.summary}</p> : ''}
                                            </ScrollAnimation>
                                        </BlogEntry>
                                    )
                                })
                            }
                        </BlogEntries>
                    </BlogBlock>
                </Container>
            </Layout>
        </>
    )
})

const BlogEntry = styled(Link)`
    width: 33.3333%;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    padding: 1.5rem 3rem;
    background-color: ${colors.tertiary}; 
    color: ${colors.quaternary};
    text-decoration: none;
    ${breakpoints.md} {
        width: 100%;
    }
    &:nth-child(1) {
        background-color: ${colors.primary}; 
        color: ${colors.tertiary};
    }
    &:nth-child(3) {
        background-color: ${colors.secondary};
        color: ${colors.tertiary};
    }
    h2 {
        letter-spacing: .2rem;
        font-size: 1.4rem;
    }
    p {
        font-size: 1rem;
    }
    h2, p {
        transition: ${transitions.button};
    }
    &:hover {
        h2, p {
            transform: scale(1.1);
        }
    }
`

const BlogEntries = styled.div`
    display: flex;
    flex-direction: row;
    min-height: 10rem;
    ${breakpoints.md} {
        flex-wrap: wrap;
    }
`

const BlogBlock = styled.div`
    width: 100%;
    background-color: ${colors.quaternary}; 
    color: ${colors.tertiary}; 
`
const BlogTitle = styled.div`
    padding: 1rem 0;
    margin: auto;
    width: fit-content;
    font-size: 4rem;
    font-weight: bold;
    text-align: center;
    ${breakpoints.md} {
        font-size: 2.5rem;
    }
`

const CenterBlock = styled.section`
    display: flex;
    width: 100%;
    padding: 3rem 0;

    ${breakpoints.md} {
        flex-direction: column;
    }    
`

const CenterBlockTitle = styled.div`
    width: 40%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    ${breakpoints.md} {
        width: 100%;
        min-height: 10rem;
    }
    h2 {
        margin: auto 0 auto 3rem;
        font-size 3.5rem;
        text-align: right;
        ${breakpoints.md} {
            max-width: 90%;
            margin: auto;
            text-align: center;
            font-size 2.5rem;
        }

        a {
            display: block;
            margin: 1rem 0 1rem auto;
            width: fit-content;
            padding: 1rem 2rem;
            background-color: ${colors.quaternary};
            color: ${colors.tertiary};
            text-decoration: none;
            font-size: 1rem;
            font-family: Avenir, sans-serif;
            text-transform: uppercase;
            ${breakpoints.md} {
                margin: 1rem auto;
            }
        }
    }
`

const CenterBlockText = styled.div`    
    width: 60%;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    overflow: hidden;
    > div {
        max-width: 90%;
        margin: auto auto auto 3rem;
        ${breakpoints.md}{
            max-width: 80%;
            //padding: 6vh 0;
        }
        h4 {
            font-size: 1.3rem;
            font-weight: 500;
            margin: 1.5rem 0 0 0;
            color: ${colors.secondary};
        }
        p {
            font-size: 1rem;
        }
    }
    ${breakpoints.md}{
        position: initial;
        height: auto;
        width: 100%;
    }
`

const ListBlockSection = styled.div`
    margin: auto;
    overflow: hidden;
    margin: 0
    ${breakpoints.md} {
        width: 100%;
    }    
    ul {
        list-style-type: disc;
    }    
`
const ListBlock = styled.div`
    padding: 2rem 0 0;
    white-space: pre-wrap;
    button {
        border: 0;
        background: transparent;
        cursor: pointer;
        position: relative;
        padding: 0px;
        text-align: left;
        display: flex;

        h3 {
            font-size: 1.3rem;
            font-weight: 500;
            font-family: Avenir, sans-serif;
            color: ${colors.secondary};
            margin: 0 0 0 1rem;
        }

        svg {
            height: 1.2rem;
            width: 1.2rem;
            min-width: 1.2rem;
            transition: all .3s ease-in-out;
        }
    }
    & > div {
        overflow: hidden;
        max-height: 0;
        transition: max-height 0.5s cubic-bezier(0, 1, 0, 1);
    }
    &.show {
        > div {
            max-height: 50rem;
            transition: max-height 1s ease-in-out;
        }
        svg {
            transform: rotate(180deg);
        }
    }
`

const Container = styled.div`
    background-color: ${colors.quinary};
`

const Title = styled.div`
    position: relative;
    width: 50%;
    background-color: ${colors.primary};
    display: flex;
    justify-content: center;
    align-items: center;
    ${breakpoints.md} {
        width: 100%;
        min-height: 30rem;
        order: 1;
        .show {
            max-width: 15rem;
        }
    }
    h1 {
        font-size: 4rem;
        color: ${colors.tertiary};
        max-width: 70%;
        margin: auto;
        font-size 3.5rem;
        ${breakpoints.md} {
            margin: auto;
            max-width: 90%;
            text-align: center;
            font-size 2.5rem;
        }
    }
    &:hover > div {
        max-width: 15rem;
    }
`

export const query = graphql`
    query($language: String) {
        contentfulSection(name: {eq: "Ecommerce Services"}, node_locale: {eq: $language}) {
            id
            title
            seo {
                title
                slug
                attributes {
                    description
                    keywords
                }
            }
            data {
                blogSectionTitle
                mainSectionTitle
            }
            cta {
                id
                href
                value
            }
            media {
                id
                title
                fluid(quality: 90, maxWidth: 1920) {
                    ...GatsbyContentfulFluid_withWebp_noBase64            
                }
            }
            animatedHeroText {
                text1
                text2
                text3
                text4
                text5
                text6
            }
            listBlockSection {
                title
                text {
                    json
                }
            }
        }
        blogEntries: allContentfulBlogEntry(sort: {fields: createdAt, order: DESC}, limit: 3, filter: {tags: {in: ["consultoria", "transformacion digital", "trends", "marketing digital", "digitaltransformation", "deploy"]}, node_locale: {eq: $language}}) {
            edges {
                node {
                    id
                    title
                    slug
                    summary {
                        summary
                    }
                }
            }
        }
    }

`
